import * as React from 'react';
import { Toast, ToastBody, ToastHeader, Spinner } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Afr, FactTable, FactTableRow, Fact, getSelectedAfr, getAdminEventProperty, AffiliateReporting, isFundGroupOriented } from '../../redux/AfrSlice';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import dateHelper from '../../utils/dateHelper';
import utilityHelper from '../../utils/utilityHelper';
import ToastReporter from './ToastReporter'

const getTables = function (afr: Afr, tableName: string) {
    var tableList: Array<FactTable> = [];
    var table: FactTable | null = null;
    if (afr.currentversion) {
        afr.currentversion.tables.forEach(function (tbl) {
            if (tbl.structure.name.includes(tableName)) {
                tableList.push(tbl);
            }
        });
    }
    return tableList;
}

const getFactCount = function (afr: Afr, tableNameSet: string) {
    var factCount = 0;
    tableNameSet.split("+").map(tableName => {
        var possibleTables: Array<FactTable> = getTables(afr, tableName);
        for (var possibleTable of possibleTables) {
            var table: FactTable = possibleTable;
            if (table.rows && table.rows.length > 0) {
                table.rows.forEach(function (row) {
                    factCount += row.facts.length;
                });
            }
        }
    })
    return factCount;
}
const getDimensionalFactCount = function (afr: Afr, tableNameSet: string, dimensionalIds: string) {
    var factCount = 0;
    var dimensionSet: any = {};
    dimensionalIds.split("+").forEach(function (id) {
        dimensionSet[id] = true;
    });
    tableNameSet.split("+").map(tableName => {
        var possibleTables: Array<FactTable> = getTables(afr, tableName);
        for (var possibleTable of possibleTables) {
            var table: FactTable = possibleTable;
            if (table.rows && table.rows.length > 0) {
                table.rows.forEach(function (row) {
                    row.facts.forEach(function (f) {
                        if (f.dimensions) {
                            f.dimensions.forEach(function (dim) {
                                if (dim.member && dimensionSet.hasOwnProperty(dim.member.code)) {
                                    factCount += 1;
                                }
                            })
                        }
                    })
                });
            }
        }
    })
    return factCount;
}

const getAfrTitle = function (afr: Afr) {
    if (!afr) {
        return "No report selected";
    }
    var result: string = "Current ";
    switch (afr.taxonomyType) {
        case "AFR":
            result += "Afr"
            break;

        default:
            result += utilityHelper.toTitleCase(afr.taxonomyType);
            break;
    }
    return result;
}
const getAfrProperties = function (afr: Afr) {
    var displayStatus = utilityHelper.getStatusDescription(afr.status);
    if (afr.status === "REPORT_MODE") {
        return [
            { key: "Year", value: afr.year },
            { key: "Status", value: displayStatus }
        ];
    }
    if (afr.status === "REPORTED_BY_PRIMARY") {
        if (afr.affiliateReportingForAfr) {
            var reporting: AffiliateReporting = afr.affiliateReportingForAfr;
            return [
                { key: "Year", value: afr.year },
                { key: "Status", value: displayStatus },
                { key: "Reporting Type", value: reporting.reportingType },
                { key: "Audit", value: reporting.audit ? "Audit" : "none" }
            ]
        } else {
            return [
                { key: "Year", value: afr.year },
                { key: "Status", value: displayStatus },
                { key: "Reporting Type", value: "unknown" }
            ]
        }
    }
    var result : any[] = [];
    var receivedKey: string = "AFR Received Date";
    switch (afr.taxonomyType) {
        case TaxonomyTypeEnum.AFR:
            if (isFundGroupOriented(afr)) {
                result = [
                    { key: "Year", value: afr.year },
                    { key: "Status", value: displayStatus },
                    { key: "Audit", value: utilityHelper.toProperCase(afr.audittype) },
                    { key: "Version", value: afr.currentversion.versionNumber },
                    { key: "Governmental", value: getDimensionalFactCount(afr, "balance+revenue+expenditure", "001+100+200+300+050") },
                    { key: "Proprietary", value: getDimensionalFactCount(afr, "balance+revenue+expenditure","400+500") },
                    { key: "Fiduciary", value: getDimensionalFactCount(afr, "balance+revenue+expenditure", "600+650+700+750") }
                ];
                if (afr.affiliateReporting && afr.affiliateReporting.length > 0) {
                    result.push({ key: "Component Units", value: getDimensionalFactCount(afr, "balance+revenue+expenditure", "000") });
                }
            } else {
                result = [
                    { key: "Year", value: afr.year },
                    { key: "Status", value: displayStatus },
                    { key: "Audit", value: utilityHelper.toProperCase(afr.audittype) },
                    { key: "Version", value: afr.currentversion.versionNumber },
                    { key: "Balance Sheet", value: getFactCount(afr, "balance") },
                    { key: "Revenues", value: getFactCount(afr, "revenue") },
                    { key: "Expenditures", value: getFactCount(afr, "expenditure") }
                ];
            }
            break;

        case TaxonomyTypeEnum.COURT_REPORTING_939:
            receivedKey = "Court Report 939.185 Received Date";
            result = [
                { key: "Year", value: afr.year },
                { key: "Status", value: displayStatus },
                { key: "Quarter", value: afr.logicalPeriod ? afr.logicalPeriod.replace("Q_", "") : "none" },
                { key: "Version", value: afr.currentversion.versionNumber },
                { key: "Revenues", value: getFactCount(afr, "revenue") },
                { key: "Expenditures", value: getFactCount(afr, "expenditure") }
            ];
            break;

        case TaxonomyTypeEnum.COURT_REPORTING_29:
            receivedKey = "Court Report 29.0085 Received Date";
            result = [
                { key: "Year", value: afr.year },
                { key: "Status", value: displayStatus },
                { key: "Version", value: afr.currentversion.versionNumber },
                { key: "Part A", value: getFactCount(afr, "cr29-court-reporting-local-requirements-collections") },
                { key: "Part B", value: getFactCount(afr, "cr29-court-reporting-non-recurring-expenditures") },
                { key: "Part C", value: getFactCount(afr, "cr29-court-reporting-facility-cost+cr29-court-reporting-communications-systems-and-services+cr29-court-reporting-existing-systems") },
                { key: "Part D", value: getFactCount(afr, "cr29-court-reporting-local-requirements") },
                { key: "Part E", value: getFactCount(afr, "cr29-court-reporting-other-operating-court-related-expenditures") },
            ];
            break;
    }
    const version = afr.currentversion;
    if (version) {
        if (version.amendmentNumber) {
            result.push({ key: "Amendment", value: version.amendmentNumber })
        }
        if (version.revisionNumber) {
            result.push({ key: "Revision", value: version.revisionNumber })
        } else {
            result.push({ key: "Revision", value: "" })
        }
        if (version.audit && version.audit.datePerformed && version.audit.datePerformed.length > 0) {
            let auditDate: string = dateHelper.stringToDateString(version.audit.datePerformed);
            result.splice(3, 0, { key: "Audit Performed Date", value: auditDate });
            if (afr.auditReceivedDate) {
                let auditReceivedDate: string = dateHelper.stringToDateString(afr.auditReceivedDate);
                result.splice(3, 0, { key: "Audit Received Date", value: auditReceivedDate });
            } else {
                result.splice(3, 0, { key: "Audit Received Date", value: "none" });
            }
        }
        if (afr.afrReceivedDate) {
            let afrReceiveDate: string = dateHelper.stringToDateString(afr.afrReceivedDate);
            result.splice(2, 0, { key: receivedKey, value: afrReceiveDate });
        }
        /*
        if (version.statusEvents) {
            var foundEntitySubmissionEvent = version.statusEvents.find((element) => element.statevalue == "afr status: Certified by entity");
            if (foundEntitySubmissionEvent) {
                let afrReceiveDate: string = dateHelper.stringToDateString(foundEntitySubmissionEvent.timestamp);
                result.splice(2, 0, { key: "AFR Received Date", value: afrReceiveDate });
            }
        }
        */
    }
    if (afr.taxonomyType === "AFR") {
        var jlacNonCompliant = getAdminEventProperty(afr, "jLACNonCompliance");
        if (jlacNonCompliant !== true) {
            result.push({ key: "JLAC Compliance", value: "Yes" });
        } else {
            result.push({ key: "JLAC Compliance", value: "No" });
        }
    }

    

    return result;
}

const AfrReporter = (props: any) => {
    //TODO create a generic reporter component to actually render the topic and details.
    //props should include title and detail list
    const dispatch = useAppDispatch();
    var afr: Afr | null = useAppSelector(getSelectedAfr);
    const [detailData, setDetailData] = React.useState<any[]>([]);

    React.useEffect(() => {
        if (afr) {
            if (afr.status === "REPORT_MODE") {
                setDetailData( [
                    { key: "Year", value: afr.year },
                    { key: "Status", value: afr.status.replace("_", " ") }
                ]);
            } else {
                setDetailData(afr.currentversion ? getAfrProperties(afr) : [
                    { key: "Year", value: afr.year },
                    { key: "Status", value: utilityHelper.toProperCase(afr.status.replace("_", " ")) },
                    { key: "Audit", value: utilityHelper.toProperCase(afr.audittype) },
                    { key: "Version", value: 0 },
                    { key: "Balance Sheet", value: 0 },
                    { key: "Revenues", value: 0 },
                    { key: "Expenditures", value: 0 }
                ]);
            }
        }
    }, [afr]);
    return (
        <div>
            {afr != null ?
                <ToastReporter topic={getAfrTitle(afr)} style="" details={detailData} showReadMoreMaxChars={0} />
                : (<div></div>)
            }
        </div>
    )
}

export default AfrReporter;